import { useState, useEffect, useRef } from 'react';

/**
 * Hook that counts down to a promo end time.
 * Returns { timeLeft, isExpired } where timeLeft is "HH:MM:SS" string.
 * Calls onExpire callback when countdown reaches 0.
 */
export function usePromoCountdown(promoEndsAt, onExpire) {
  const [timeLeft, setTimeLeft] = useState('');
  const [isExpired, setIsExpired] = useState(false);
  const onExpireRef = useRef(onExpire);
  onExpireRef.current = onExpire;

  useEffect(() => {
    if (!promoEndsAt) return;

    const endTime = new Date(promoEndsAt).getTime();

    const update = () => {
      const now = Date.now();
      const diff = endTime - now;

      if (diff <= 0) {
        setTimeLeft('00:00:00');
        setIsExpired(true);
        if (onExpireRef.current) {
          onExpireRef.current();
        }
        return false;
      }

      const hours = Math.floor(diff / 3600000);
      const minutes = Math.floor((diff % 3600000) / 60000);
      const seconds = Math.floor((diff % 60000) / 1000);

      setTimeLeft(
        `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
      );
      return true;
    };

    if (!update()) return;

    const intervalId = setInterval(() => {
      if (!update()) {
        clearInterval(intervalId);
      }
    }, 1000);

    return () => clearInterval(intervalId);
  }, [promoEndsAt]);

  return { timeLeft, isExpired };
}
